import type { ApiResponse, Contact, ReplyPayload, ReplyType } from '@/types'

export function isSuccess(response: ApiResponse) {
  return (
    response.Success === true ||
    response.success === true ||
    response.Code === 200 ||
    response.code === 200 ||
    response.Code === 0 ||
    response.code === 0 ||
    response.Code === 1
  )
}

export function getMessage(response: ApiResponse) {
  return response.Message ?? response.message ?? response.Text ?? response.text ?? ''
}

export function getData<T = unknown>(response: ApiResponse<T>) {
  return response.Data ?? response.data
}

export function unwrapString(value: unknown): string {
  if (typeof value === 'string') return value
  if (typeof value === 'number') return String(value)
  if (!value || typeof value !== 'object') return ''
  const object = value as Record<string, unknown>
  return (
    unwrapString(object.String_) ||
    unwrapString(object.string_) ||
    unwrapString(object.String) ||
    unwrapString(object.string) ||
    unwrapString(object.Value) ||
    unwrapString(object.value)
  )
}

export function getAnyString(source: unknown, keys: string[]): string {
  if (!source || typeof source !== 'object') return ''
  const object = source as Record<string, unknown>
  for (const key of keys) {
    const text = unwrapString(object[key])
    if (text) return text
  }
  for (const value of Object.values(object)) {
    const nested = getAnyString(value, keys)
    if (nested) return nested
  }
  return ''
}

export function getAnyNumber(source: unknown, keys: string[]): number | null {
  const text = getAnyString(source, keys)
  if (!text) return null
  const value = Number(text)
  return Number.isFinite(value) ? value : null
}

export function formatFriendRelation(response: ApiResponse | undefined) {
  const relation = getAnyNumber(response, ['FriendRelation', 'friendRelation'])
  const labels: Record<number, string> = {
    0: '正常',
    1: '已被对方删除',
    4: '已拉黑对方',
    5: '被对方拉黑',
  }
  if (relation === null) return '检测结果: 未知'
  return `检测结果: ${labels[relation] || '未知'}`
}

export function parseAccount(payload: unknown) {
  const wxid = getAnyString(payload, ['userName', 'UserName', 'Wxid', 'WxId', 'wxid', 'wxId'])
  if (!wxid) return null
  return {
    wxid,
    nickName: getAnyString(payload, ['nickName', 'NickName', 'Nickname']) || wxid,
    alias: getAnyString(payload, ['alias', 'Alias', 'Alais']),
    avatar: getAnyString(payload, [
      'headImgUrl',
      'HeadImgUrl',
      'HeadUrl',
      'headUrl',
      'BigHeadImgUrl',
      'SmallHeadImgUrl',
    ]),
  }
}

export function getQrPollingMessage(response: ApiResponse) {
  const data = getData(response)
  const statusText = getAnyString(data, ['state', 'State', 'status', 'Status'])
  const status = statusText ? Number(statusText) : null
  const message = getMessage(response)
  const captchaType = getAnyString(data, ['type', 'Type'])
  if ((response.Code === -6 || response.code === -6) && captchaType === 'wtlogin_image_captcha') {
    return '需要输入图片验证码，请在接口返回的 imageData 中查看验证码'
  }
  if (status === 2) return message && message !== '成功' ? message : '已确认登录，正在初始化...'
  if (status === 1) return '已扫码，请在手机微信点击确定登录'
  if (status === 0) return '请使用手机微信扫码登录'
  if (message && message !== '成功') return message
  return isSuccess(response) ? '请使用手机微信扫码登录' : '等待手机扫码确认'
}

export function displayName(contact: Contact) {
  return contact.remark || contact.nickName || contact.name || contact.wxid
}

const AVATAR_PALETTE = [
  '#07c160',
  '#06ad56',
  '#2d8cf0',
  '#5d6dff',
  '#8b5cf6',
  '#d946ef',
  '#f43f5e',
  '#f97316',
  '#f59e0b',
  '#10b981',
  '#0ea5e9',
  '#6366f1',
]

function hashSeed(seed: string): number {
  let hash = 5381
  for (let i = 0; i < seed.length; i++) {
    hash = ((hash << 5) + hash + seed.charCodeAt(i)) >>> 0
  }
  return hash
}

function initialsFor(seed: string): string {
  const trimmed = (seed || '').trim()
  if (!trimmed) return '?'
  // For Chinese/CJK names take the last char; otherwise take the first ASCII alnum.
  const cjk = trimmed.match(/[\u4e00-\u9fa5]/g)
  if (cjk && cjk.length > 0) return cjk[cjk.length - 1]
  const ascii = trimmed.match(/[A-Za-z0-9]/)
  if (ascii) return ascii[0].toUpperCase()
  return trimmed[0]
}

export function avatarFor(seed: string) {
  const key = seed || 'anonymous'
  const color = AVATAR_PALETTE[hashSeed(key) % AVATAR_PALETTE.length]
  const initial = initialsFor(key)
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64" width="64" height="64"><rect width="64" height="64" rx="8" fill="${color}"/><text x="50%" y="50%" dy="0.35em" text-anchor="middle" font-family="-apple-system,BlinkMacSystemFont,Segoe UI,sans-serif" font-size="28" font-weight="600" fill="#ffffff">${initial}</text></svg>`
  return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`
}

export function formatMessageTime(createdAt: number) {
  return new Date(createdAt).toLocaleString([], {
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
}

export function emptyReplyPayload(type: ReplyType = 'text'): ReplyPayload {
  return { type, content: '', parts: [] }
}

export function payloadText(payload: ReplyPayload): string {
  if (payload.parts?.length) {
    return [payload.content?.trim(), ...payload.parts.map(payloadText)].filter(Boolean).join(' ')
  }
  if (payload.type === 'image') return `[图片] ${payload.fileName || ''}`.trim()
  if (payload.type === 'voice') return '[语音]'
  if (payload.type === 'video') return `[视频] ${payload.fileName || ''}`.trim()
  if (payload.type === 'link') return `[链接] ${payload.title || payload.url || ''}`.trim()
  if (payload.type === 'file') return `[文件] ${payload.fileName || ''}`.trim()
  return payload.content || ''
}

export function formatFileSize(size = 0) {
  if (size >= 1024 * 1024) return `${(size / 1024 / 1024).toFixed(1)}M`
  if (size >= 1024) return `${Math.round(size / 1024)}K`
  return `${size}B`
}

export function readFileAsDataUrl(file: File) {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(String(reader.result || ''))
    reader.onerror = () => reject(reader.error)
    reader.readAsDataURL(file)
  })
}

export function getVideoPoster(file: File) {
  return new Promise<string>((resolve) => {
    const video = document.createElement('video')
    const url = URL.createObjectURL(file)
    video.preload = 'metadata'
    video.muted = true
    video.src = url
    video.onloadeddata = () => {
      video.currentTime = Math.min(0.1, video.duration || 0)
    }
    video.onseeked = () => {
      const canvas = document.createElement('canvas')
      canvas.width = video.videoWidth || 320
      canvas.height = video.videoHeight || 180
      canvas.getContext('2d')?.drawImage(video, 0, 0, canvas.width, canvas.height)
      URL.revokeObjectURL(url)
      resolve(canvas.toDataURL('image/jpeg', 0.8))
    }
    video.onerror = () => {
      URL.revokeObjectURL(url)
      resolve('')
    }
  })
}

export const accountStorageKey = 'wechat-web-account'
export const lastWxidStorageKey = 'wechat-web-last-wxid'
const unreadCountsStoragePrefix = 'wxproto-desk-unread-counts:'

// Scope selected-account storage per console user to avoid cross-user restore.
let accountStorageScope = ''

export function setAccountStorageScope(consoleUserUuid: string) {
  accountStorageScope = consoleUserUuid || ''
}

function scopedStorageKey(base: string): string {
  return accountStorageScope ? `${base}:${accountStorageScope}` : base
}

export function readStoredAccount() {
  try {
    const raw = window.localStorage.getItem(scopedStorageKey(accountStorageKey))
    if (!raw) return null
    const parsed = JSON.parse(raw) as { wxid: string; nickName: string; alias?: string; avatar?: string }
    return parsed.wxid ? parsed : null
  } catch {
    return null
  }
}

export function saveStoredAccount(account: { wxid: string; nickName: string; alias?: string; avatar?: string } | null) {
  if (!account) {
    window.localStorage.removeItem(scopedStorageKey(accountStorageKey))
    return
  }
  window.localStorage.setItem(scopedStorageKey(accountStorageKey), JSON.stringify(account))
  window.localStorage.setItem(scopedStorageKey(lastWxidStorageKey), account.wxid)
}

/** Clear selected account, last-wxid keys, and unread counters on console logout. */
export function clearStoredConsoleUserData() {
  try {
    window.localStorage.removeItem(scopedStorageKey(accountStorageKey))
    window.localStorage.removeItem(scopedStorageKey(lastWxidStorageKey))
    window.localStorage.removeItem(accountStorageKey)
    window.localStorage.removeItem(lastWxidStorageKey)
    const doomed: string[] = []
    for (let index = 0; index < window.localStorage.length; index += 1) {
      const key = window.localStorage.key(index)
      if (key && key.startsWith(unreadCountsStoragePrefix)) doomed.push(key)
    }
    for (const key of doomed) window.localStorage.removeItem(key)
  } catch {
    // Storage may be unavailable (private mode); logout must not fail on it.
  }
}

export function filterContacts(contacts: Contact[], query: string) {
  const lowerQuery = query.trim().toLowerCase()
  if (!lowerQuery) return contacts
  return contacts.filter((contact) =>
    [contact.remark, contact.nickName, contact.name, contact.wxid]
      .filter(Boolean)
      .some((value) => String(value).toLowerCase().includes(lowerQuery)),
  )
}
